import { useId, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { workspaceCopy } from '../../merchant/copy.js';
import {
  ATTENTION_STATUSES,
  formatDate,
  formatDuration,
  formatRelative,
  lineActionLabel,
  lineStatusLabel,
  videoStatusLabel,
  type LineSummary,
} from '../../merchant/model.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';
import {
  Button,
  ButtonLink,
  Callout,
  Card,
  EmptyState,
  Eyebrow,
  PageHead,
  Pix,
  PixelCluster,
  PlusIcon,
  Poster,
  Tag,
} from '../../merchant/ui/primitives.js';

const ATTENTION_ORDER: Readonly<Record<string, number>> = {
  'needs-input': 0,
  failed: 1,
  'ready-to-choose': 2,
};

/**
 * Home, from the wireframe: a greeting, the one thing that needs the
 * merchant, three active Production Lines, recently completed videos and
 * the Business Profile completeness card — all from the shared snapshot.
 */
export function MerchantHomePage(): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const workspace = useWorkspace();
  const { businessName } = useMerchantWorkspaceIdentity();
  const navigate = useNavigate();
  const titleId = useId();
  const workspaceName = businessName?.trim() || copy.nav.yourBusiness;
  const hour = new Date().getHours();
  const greeting = `${copy.home.greet[hour < 12 ? 0 : hour < 17 ? 1 : 2]}${language === 'ar' ? '، ' : ', '}${workspaceName}`;
  const domain = (() => {
    const website = workspace.profile?.business.sources.find(
      (s) => s.platform === 'WEBSITE',
    );
    if (website === undefined) return '';
    try {
      return new URL(website.canonicalUrl).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  })();

  const attention = useMemo(() => {
    const candidates = workspace.lines.filter((line) =>
      ATTENTION_STATUSES.has(line.status),
    );
    return [...candidates].sort(
      (a, b) => (ATTENTION_ORDER[a.status] ?? 9) - (ATTENTION_ORDER[b.status] ?? 9),
    )[0];
  }, [workspace.lines]);

  const homeLines = useMemo(
    () =>
      workspace.lines
        .filter((line) => line.status !== 'draft' && line.status !== 'archived')
        .slice(0, 3),
    [workspace.lines],
  );
  const homeVideos = useMemo(
    () => workspace.videos.filter((video) => video.status === 'ready').slice(0, 3),
    [workspace.videos],
  );

  const payload = workspace.profile?.profile?.payload;
  const sections = [
    Boolean(payload?.identity.value && payload.niche.value && payload.geography.value),
    (payload?.offers.length ?? 0) > 0,
    false, // Creative taste calibration is not available yet.
    (workspace.profile?.business.sources.length ?? 0) > 0,
  ];
  const done = sections.filter(Boolean).length;
  const pct = Math.round((done / sections.length) * 100);
  const hint = !sections[1]
    ? copy.home.hintOffers
    : !sections[2]
      ? copy.home.hintTaste
      : copy.home.hintDone;
  const completenessAction = !sections[1]
    ? copy.home.addOffer
    : copy.home.reviewProfile;
  const completenessHref = !sections[1]
    ? '/business-profile/offers'
    : '/business-profile';

  const attentionDescription = (line: LineSummary): string => {
    if (line.status === 'failed') return copy.home.attentionFailed;
    if (line.status === 'ready-to-choose') return copy.home.attentionRecs;
    if (line.action === 'card') return copy.home.attentionCard;
    return copy.home.attentionProfile;
  };

  return (
    <section className="mj-ws-page" aria-labelledby={titleId} data-screen-label="Home">
      <PageHead
        eyebrow={
          <>
            {workspaceName}
            {domain ? (
              <>
                {' · '}
                <span dir="ltr">{domain}</span>
              </>
            ) : null}
          </>
        }
        title={greeting}
        titleId={titleId}
        intro={copy.home.intro}
        action={
          <Button
            variant="primary"
            onClick={() => void navigate('/production-lines/new')}
            disabled={workspace.creationDestination === null && !workspace.loading}
          >
            <PlusIcon />
            {copy.home.newLine}
          </Button>
        }
      />

      {workspace.failed ? (
        <Callout className="mj-ws-mt-24" role="status">
          <span className="mj-ws-callout-copy mj-ws-sm">
            {copy.home.summaryUnavailable}
          </span>
          <Button small onClick={() => void workspace.refresh()}>
            {copy.common.tryAgain}
          </Button>
        </Callout>
      ) : null}

      {attention !== undefined ? (
        <>
          <Eyebrow className="mj-ws-section-label">{copy.home.attention}</Eyebrow>
          <Card className="mj-ws-row is-attention" as="section">
            <Pix status={attention.status} />
            <div className="mj-ws-callout-copy">
              <div className="mj-ws-strong">{attention.title}</div>
              <div className="mj-ws-muted mj-ws-sm">
                {attentionDescription(attention)}
              </div>
            </div>
            <ButtonLink to={attention.actionHref} variant="primary">
              {lineActionLabel(attention.action, copy)}
            </ButtonLink>
          </Card>
        </>
      ) : null}

      <Eyebrow className="mj-ws-section-label">{copy.home.activeLines}</Eyebrow>
      {workspace.loading ? (
        <p className="mj-ws-loading" role="status">
          {copy.common.loading}
        </p>
      ) : homeLines.length > 0 ? (
        <div className="mj-ws-g3">
          {homeLines.map((line) => (
            <Card key={line.id} as="article" className="mj-ws-col">
              <div
                className="mj-ws-row is-between"
                style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}
              >
                <h3 className="mj-ws-h3">{line.title}</h3>
                <Tag status={line.status}>{lineStatusLabel(line.status, copy)}</Tag>
              </div>
              <p className="mj-ws-muted mj-ws-sm" style={{ flex: 1 }}>
                {line.prompt
                  ? `“${line.prompt}”`
                  : line.domain || copy.common.noBriefYet}
              </p>
              <div className="mj-ws-row is-between">
                <span className="mj-ws-muted mj-ws-xs mj-ws-tnum">
                  {formatRelative(line.updatedAt, copy, language)} ·{' '}
                  {copy.common.videosCount(line.videos)}
                </span>
                <ButtonLink to={line.actionHref} small>
                  {lineActionLabel(line.action, copy)}
                </ButtonLink>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          body={copy.home.noLines(workspaceName)}
          action={
            <Button
              variant="primary"
              onClick={() => void navigate('/production-lines/new')}
            >
              {copy.home.newLine}
            </Button>
          }
        >
          <PixelCluster />
        </EmptyState>
      )}

      <div className="mj-ws-g2 mj-ws-mt-32">
        <section aria-labelledby={`${titleId}-videos`}>
          <Eyebrow id={`${titleId}-videos`} className="mj-ws-mt-12">
            {copy.home.recentVideos}
          </Eyebrow>
          <Card padding={0} className="mj-ws-mt-12 mj-ws-vrows">
            {homeVideos.length > 0 ? (
              homeVideos.map((video) => (
                <button
                  key={video.id}
                  type="button"
                  className="mj-ws-vrow"
                  onClick={() =>
                    void navigate(
                      video.lineId === null
                        ? '/videos'
                        : `/videos/line/${encodeURIComponent(video.lineId)}?video=${encodeURIComponent(video.id)}`,
                    )
                  }
                >
                  <Poster mini posterUrl={video.posterUrl} state="playable" />
                  <span className="mj-ws-vrow-copy">
                    <span className="mj-ws-vrow-title">{video.title}</span>
                    <span className="mj-ws-muted mj-ws-xs mj-ws-tnum">
                      {video.lineTitle} ·{' '}
                      {formatDuration(video.durationSeconds) ?? copy.common.dash} ·{' '}
                      {formatDate(video.date, language)}
                    </span>
                  </span>
                  <Tag status={video.status}>
                    {videoStatusLabel(video.status, copy)}
                  </Tag>
                </button>
              ))
            ) : (
              <p className="mj-ws-muted mj-ws-sm" style={{ padding: '14px 20px' }}>
                {copy.home.noVideos}
              </p>
            )}
          </Card>
        </section>
        <section aria-labelledby={`${titleId}-profile`}>
          <Eyebrow id={`${titleId}-profile`} className="mj-ws-mt-12">
            {copy.home.profile}
          </Eyebrow>
          <Card className="mj-ws-mt-12">
            <div className="mj-ws-row is-between" style={{ alignItems: 'baseline' }}>
              <span className="mj-ws-strong">
                {done === sections.length
                  ? copy.home.profileComplete
                  : copy.home.profilePct(pct)}
              </span>
              <span className="mj-ws-muted mj-ws-sm mj-ws-tnum">
                {copy.home.sections(done, sections.length)}
              </span>
            </div>
            <div className="mj-ws-meter" aria-hidden="true">
              <div style={{ width: `${String(pct)}%` }} />
            </div>
            <p className="mj-ws-muted mj-ws-sm" style={{ marginBottom: 16 }}>
              {hint}
            </p>
            <ButtonLink to={completenessHref} small>
              {completenessAction}
            </ButtonLink>
          </Card>
        </section>
      </div>
    </section>
  );
}
